/**
 * «Посеять по плану» — чем план заполняет мастер сева, и что считается
 * отклонением от утверждённого бюджета.
 *
 * Чистые функции без React: и подстановка, и сравнение план↔факт — это
 * арифметика, которую агроном читает глазами на экране, поэтому она обязана
 * быть покрыта тестами отдельно от вёрстки.
 */
import { emptyDraft, type SowDraft } from '../sow/sowMath';
import type { FieldSeasonPlan, SowPrefill } from './api';

/** Ключ культуры для выпадашки мастера: номенклатура хозяйства бьёт канон. */
export function planCropKey(prefill: SowPrefill | null): string {
  if (!prefill) return '';
  if (prefill.local_catalog_item_id) return prefill.local_catalog_item_id;
  return prefill.crop_id ? `crop:${prefill.crop_id}` : '';
}

/**
 * Единица густоты плана → единица мастера.
 *
 * Техкарта хранит густоту с символом единицы («шт/га», «млн шт/га», «кг/га»),
 * мастер — перечислением. Не распознали символ — оставляем дефолт мастера и
 * НЕ подставляем цифру наугад в чужую единицу: 60 000 «млн шт/га» это бред,
 * который агроном заметит не сразу.
 */
export function rateUnitFromSymbol(
  symbol: string | null,
): SowDraft['seedingRateUnit'] | null {
  if (!symbol) return null;
  const s = symbol.trim().toLowerCase().replace(/ё/g, 'е');
  if (s.includes('млн')) return 'mln_pcs_ha';
  if (s.startsWith('кг')) return 'kg_ha';
  if (s.startsWith('шт') || s.includes('шт/га')) return 'pcs_ha';
  return null;
}

/**
 * Черновик мастера, заполненный планом (экран 4 прототипа).
 *
 * Заполняем ровно то, что бюджет ЗНАЛ: культуру, площадь, участок, густоту и
 * норму высева из техкарты, ориентировочную дату — начало агроокна сева.
 * Чего бюджет знать не мог — гибрид, точную дату, фактически засеянную
 * площадь — агроном подтверждает или правит сам.
 *
 * `fieldAreaHa` — площадь контура, она остаётся «по контуру» в форме: план мог
 * быть заведён на часть поля, и подменять контур плановой площадью нельзя.
 */
export function draftFromPlan(
  plan: FieldSeasonPlan,
  fieldAreaHa: number | null,
): SowDraft {
  const prefill = plan.sow_prefill;
  const contour = prefill?.subzone_id ? (prefill.area_ha ?? null) : fieldAreaHa;
  const unit = rateUnitFromSymbol(prefill?.seeding_density_unit ?? null);
  return {
    ...emptyDraft(),
    subzoneId: prefill?.subzone_id ?? null,
    cropKey: planCropKey(prefill ?? null),
    cropName: prefill?.crop_name ?? plan.crop_name ?? undefined,
    contourAreaHa: contour,
    areaHa: prefill?.area_ha ?? contour,
    sowingDate: prefill?.window_starts_on ?? null,
    seedingRate: unit ? (prefill?.seeding_density ?? null) : null,
    seedingRateUnit: unit ?? 'pcs_ha',
    seedingNormKgHa: prefill?.seeding_norm ?? null,
  };
}

export interface PlanDeviation {
  field: 'crop' | 'area' | 'date';
  label: string;
  planned: string;
  actual: string;
  note: string;
}

const fmtHa = (v: number | null | undefined): string =>
  v == null ? '—' : `${v.toLocaleString('ru-RU', { maximumFractionDigits: 1 })} га`;

/**
 * Чем сев расходится с утверждённым бюджетом (экран 5 прототипа).
 *
 * Граница отклонения — предложение Р3-2 прототипа: **отклонение это то, что
 * двигает производственную программу** — культура, площадь и факт
 * неисполнения. Густота и гибрид отклонением НЕ считаются (это агрономическая
 * свобода), сев вне агроокна — мягкая пометка.
 *
 * Функция ничего не блокирует: она показывает агроному, что он делает иначе,
 * ДО сохранения. Обязательная причина и её хранение — следующий шаг моста, он
 * требует миграции (`sowing_deviations`) и ответа founder по Р3-1/Р3-2.
 */
export function planDeviations(
  plan: FieldSeasonPlan,
  draft: SowDraft,
): PlanDeviation[] {
  const out: PlanDeviation[] = [];
  if (!plan.has_plan) return out;
  const prefill = plan.sow_prefill;

  const plannedKey = planCropKey(prefill ?? null);
  if (plannedKey && draft.cropKey && draft.cropKey !== plannedKey) {
    out.push({
      field: 'crop',
      label: 'Культура',
      planned: plan.crop_name ?? '—',
      actual: draft.cropName ?? 'другая культура',
      note: 'другая культура — план останется неисполненным',
    });
  }
  if (draft.fallowKind && plan.crop_id) {
    out.push({
      field: 'crop',
      label: 'Культура',
      planned: plan.crop_name ?? '—',
      actual: 'пар',
      note: 'по плану культура, а поле остаётся незасеянным',
    });
  }
  // Прямое направление пара. Ключ культуры у планового ПАРА пуст (ни
  // `local_catalog_item_id`, ни `crop_id`), поэтому сравнение ключей выше на
  // нём молча не срабатывает — и посев любой культуры на плановом пару
  // проходил как «по плану». Оба направления одинаково двигают
  // производственную программу, значит и отклонением обязаны быть оба.
  if (plan.is_fallow && draft.cropKey && !draft.fallowKind) {
    out.push({
      field: 'crop',
      label: 'Культура',
      planned: plan.crop_name ?? 'пар',
      actual: draft.cropName ?? 'культура',
      note: 'по плану поле под паром — посев двигает программу',
    });
  }

  const plannedArea = plan.area_ha;
  if (plannedArea != null && draft.areaHa != null) {
    const delta = Math.round((draft.areaHa - plannedArea) * 100) / 100;
    if (delta !== 0) {
      out.push({
        field: 'area',
        label: 'Площадь',
        planned: fmtHa(plannedArea),
        actual: fmtHa(draft.areaHa),
        note: `${delta > 0 ? '+' : ''}${delta} га к плану`,
      });
    }
  }

  const from = prefill?.window_starts_on ?? null;
  const to = prefill?.window_ends_on ?? null;
  if (draft.sowingDate && (from || to)) {
    if ((from && draft.sowingDate < from) || (to && draft.sowingDate > to)) {
      out.push({
        field: 'date',
        label: 'Дата сева',
        planned: [from, to].filter(Boolean).join(' — '),
        actual: draft.sowingDate,
        note: 'вне агроокна техкарты',
      });
    }
  }
  return out;
}

/** Двигает ли отклонение производственную программу (культура / площадь). */
export function shiftsProgram(deviations: PlanDeviation[]): boolean {
  return deviations.some((d) => d.field === 'crop' || d.field === 'area');
}

/**
 * Адрес для роутерного `Link` — `to` + `params` + `search` по отдельности.
 *
 * Именно так, а не собранной строкой с `?query` внутри `to`: строка требует
 * `as string` на месте использования, а он глушит типизацию роутера — опечатка
 * в пути перестаёт падать в `tsc`. Разложенный вид — канон всей кодовой базы
 * (`spp/index.tsx`, `sowing-plan/index.tsx`, `plan-variant/index.tsx`).
 */
export interface PlanRouteLink<TPath extends string, TSearch> {
  to: TPath;
  params: { year: string };
  search: TSearch;
}

/** Ссылка на техкарту плана — экран техкарт сезона, карта уже раскрыта. */
export function techCardLink(
  plan: FieldSeasonPlan,
): PlanRouteLink<'/planning/seasons/$year/tech-cards', { card: string; calc: string | undefined }> | null {
  if (!plan.tech_card || plan.season_year == null) return null;
  return {
    to: '/planning/seasons/$year/tech-cards',
    params: { year: String(plan.season_year) },
    // `calc` — расчётный контекст (ADR-071 D1): на площадь какого варианта
    // считать деньги карты. Нет варианта — ключ уходит пустым, а не строкой
    // «undefined».
    search: { card: plan.tech_card.id, calc: plan.variant?.id },
  };
}

/**
 * Ссылка «откуда этот план» — вариант плана, из которого приехала строка.
 *
 * Обратный конец моста: карточка поля отвечает «что тут по плану», а вопрос
 * «почему именно это и кто так решил» живёт на экране варианта. Без ссылки
 * агроном ищет вариант руками через раздел планирования и хозяйство —
 * четыре клика вместо одного.
 *
 * `farm` обязателен: вариант плана ведётся ПО ХОЗЯЙСТВУ, и экран без него
 * откроет первое попавшееся — то есть чужой план. Нет хозяйства или года —
 * ссылки нет: лучше её отсутствие, чем переход не туда.
 */
export function planVariantLink(
  plan: FieldSeasonPlan,
): PlanRouteLink<'/planning/seasons/$year/plan-variant', { farm: string; variant: string }> | null {
  if (!plan.variant || plan.season_year == null || !plan.farm_id) return null;
  return {
    to: '/planning/seasons/$year/plan-variant',
    params: { year: String(plan.season_year) },
    search: { farm: plan.farm_id, variant: plan.variant.id },
  };
}
